import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as BackIcon,
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { chartOfAccountService } from '../../services/chartOfAccount.service';
import api from '../../services/api';
import {
  ChartOfAccount,
  getAccountTypeLabel,
  getAccountNatureLabel,
} from '../../types/chartOfAccount';

const ChartOfAccountView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { language } = useLanguage();
  const isArabic = language === 'ar';

  const [account, setAccount] = useState<ChartOfAccount | null>(null);
  const [currentBalance, setCurrentBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const t = {
    title: isArabic ? 'تفاصيل الحساب' : 'Account Details',
    back: isArabic ? 'رجوع' : 'Back',
    edit: isArabic ? 'تعديل' : 'Edit',
    accountCode: isArabic ? 'رقم الحساب' : 'Account Code',
    accountName: isArabic ? 'اسم الحساب (English)' : 'Account Name (English)',
    accountNameAr: isArabic ? 'اسم الحساب (عربي)' : 'Account Name (Arabic)',
    parentAccount: isArabic ? 'الحساب الأب' : 'Parent Account',
    accountType: isArabic ? 'نوع الحساب' : 'Account Type',
    accountNature: isArabic ? 'طبيعة الحساب' : 'Account Nature',
    level: isArabic ? 'المستوى' : 'Level',
    currency: isArabic ? 'العملة' : 'Currency',
    openingBalance: isArabic ? 'الرصيد الافتتاحي' : 'Opening Balance',
    currentBalance: isArabic ? 'الرصيد الحالي' : 'Current Balance',
    childrenCount: isArabic ? 'عدد الحسابات الفرعية' : 'Sub-accounts Count',
    description: isArabic ? 'الوصف (English)' : 'Description (English)',
    descriptionAr: isArabic ? 'الوصف (عربي)' : 'Description (Arabic)',
    notes: isArabic ? 'ملاحظات' : 'Notes',
    createdDate: isArabic ? 'تاريخ الإنشاء' : 'Created Date',
    createdBy: isArabic ? 'أنشئ بواسطة' : 'Created By',
    modifiedDate: isArabic ? 'تاريخ التعديل' : 'Modified Date',
    modifiedBy: isArabic ? 'عدّل بواسطة' : 'Modified By',
    active: isArabic ? 'نشط' : 'Active',
    inactive: isArabic ? 'غير نشط' : 'Inactive',
    isMainAccount: isArabic ? 'حساب رئيسي' : 'Main Account',
    isFinalAccount: isArabic ? 'حساب نهائي' : 'Final Account',
    allowManualEntry: isArabic ? 'يسمح بالإدخال اليدوي' : 'Allow Manual Entry',
    requireCostCenter: isArabic ? 'يتطلب مركز تكلفة' : 'Require Cost Center',
    yes: isArabic ? 'نعم' : 'Yes',
    no: isArabic ? 'لا' : 'No',
    none: isArabic ? 'لا يوجد' : 'None',
    basicInfo: isArabic ? 'المعلومات الأساسية' : 'Basic Information',
    financialInfo: isArabic ? 'المعلومات المالية' : 'Financial Information',
    settings: isArabic ? 'الإعدادات' : 'Settings',
    auditInfo: isArabic ? 'معلومات التدقيق' : 'Audit Information',
    notFound: isArabic ? 'الحساب غير موجود' : 'Account not found',
    kwd: isArabic ? 'د.ك' : 'KWD',
  };

  useEffect(() => {
    if (id) loadAccount(parseInt(id));
  }, [id]);

  const loadAccount = async (accountId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await chartOfAccountService.getById(accountId);
      setAccount(data);
      // Fetch aggregated total balance (includes all sub-accounts for parent/grandparent accounts)
      try {
        const balanceRes = await api.get(`/ChartOfAccounts/${accountId}/total-balance`);
        if (balanceRes.data?.totalBalance !== undefined) {
          setCurrentBalance(balanceRes.data.totalBalance);
        }
      } catch {
        // Not critical — balance stays null, we fall back to openingBalance
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t.notFound);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number): string =>
    new Intl.NumberFormat(isArabic ? 'ar-KW' : 'en-GB', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(value);

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(isArabic ? 'ar-KW' : 'en-GB', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !account) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error || t.notFound}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/chart-of-accounts')}>
          {t.back}
        </Button>
      </Box>
    );
  }

  const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={500}>
        {value}
      </Typography>
    </Box>
  );

  const BoolChip = ({ value, labelTrue, labelFalse }: { value: boolean; labelTrue: string; labelFalse: string }) => (
    <Chip
      label={value ? labelTrue : labelFalse}
      size="small"
      color={value ? 'success' : 'default'}
      icon={value ? <CheckCircleIcon /> : <CancelIcon />}
    />
  );

  return (
    <Box sx={{ direction: isArabic ? 'rtl' : 'ltr' }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <AccountBalanceIcon color="primary" sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h4" fontWeight={700} color="primary.main">
              {t.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {account.accountCode} — {isArabic ? account.accountNameAr : account.accountName}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<BackIcon />} onClick={() => navigate('/chart-of-accounts')}>
            {t.back}
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/chart-of-accounts/edit/${account.accountId}`)}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            {t.edit}
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Basic Information */}
        <Box>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3 }}>
            <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ mb: 2 }}>
              {t.basicInfo}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <InfoRow label={t.accountCode} value={
              <Chip label={account.accountCode} color="primary" size="small" sx={{ fontWeight: 700 }} />
            } />
            <InfoRow label={t.accountName} value={account.accountName} />
            <InfoRow label={t.accountNameAr} value={account.accountNameAr} />
            <InfoRow label={t.parentAccount} value={
              account.parentAccountCode
                ? `${account.parentAccountCode} — ${account.parentAccountName}`
                : t.none
            } />
            <InfoRow label={t.accountType} value={
              <Chip
                label={getAccountTypeLabel(account.accountType, language)}
                size="small"
                color={
                  account.accountType === 'Asset' ? 'primary'
                  : account.accountType === 'Liability' ? 'error'
                  : account.accountType === 'Equity' ? 'warning'
                  : account.accountType === 'Revenue' ? 'success'
                  : 'default'
                }
                sx={{ fontWeight: 700 }}
              />
            } />
            <InfoRow label={t.accountNature} value={
              <Chip
                label={getAccountNatureLabel(account.accountNature, language)}
                size="small"
                variant="outlined"
                color={account.accountNature === 'Debit' ? 'primary' : 'secondary'}
              />
            } />
            <InfoRow label={t.level} value={
              <Chip label={`${isArabic ? 'المستوى' : 'Level'} ${account.level}`} size="small" variant="outlined" color="info" />
            } />
            {(account.description || account.descriptionAr) && (
              <>
                {account.description && <InfoRow label={t.description} value={account.description} />}
                {account.descriptionAr && <InfoRow label={t.descriptionAr} value={account.descriptionAr} />}
              </>
            )}
            {account.notes && <InfoRow label={t.notes} value={account.notes} />}
          </Paper>
        </Box>

        {/* Financial + Settings + Audit */}
        <Box>
          {/* Financial */}
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ mb: 2 }}>
              {t.financialInfo}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <InfoRow label={t.currency} value={account.currencyCode || t.kwd} />
            <InfoRow label={t.openingBalance} value={
              <Typography variant="body1" fontWeight={600} color="text.primary">
                {formatCurrency(account.openingBalance)} {account.currencyCode || t.kwd}
              </Typography>
            } />
            <InfoRow label={t.currentBalance} value={
              <Typography variant="body1" fontWeight={700} color="success.main">
                {formatCurrency(currentBalance !== null ? currentBalance : account.openingBalance)} {account.currencyCode || t.kwd}
              </Typography>
            } />
            <InfoRow label={t.childrenCount} value={account.childrenCount ?? 0} />
          </Paper>

          {/* Settings */}
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ mb: 2 }}>
              {t.settings}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <InfoRow label={t.active} value={
              <BoolChip value={account.isActive} labelTrue={t.active} labelFalse={t.inactive} />
            } />
            <InfoRow label={t.isMainAccount} value={
              <BoolChip value={account.isMainAccount} labelTrue={t.yes} labelFalse={t.no} />
            } />
            <InfoRow label={t.isFinalAccount} value={
              <BoolChip value={account.isFinalAccount} labelTrue={t.yes} labelFalse={t.no} />
            } />
            <InfoRow label={t.allowManualEntry} value={
              <BoolChip value={account.allowManualEntry} labelTrue={t.yes} labelFalse={t.no} />
            } />
            <InfoRow label={t.requireCostCenter} value={
              <BoolChip value={account.requireCostCenter} labelTrue={t.yes} labelFalse={t.no} />
            } />
          </Paper>

          {/* Audit */}
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3 }}>
            <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ mb: 2 }}>
              {t.auditInfo}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <InfoRow label={t.createdDate} value={formatDate(account.createdDate)} />
            <InfoRow label={t.createdBy} value={account.createdByName || '-'} />
            <InfoRow label={t.modifiedDate} value={formatDate(account.modifiedDate)} />
            <InfoRow label={t.modifiedBy} value={account.modifiedByName || '-'} />
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default ChartOfAccountView;
